import type { ClientAdvisorStatus } from '@agio/core/domain';
import { Card, EmptyState } from '../../Card.js';
import { Table, THeadRow, TH, TD, TR, Ltr } from '../../ui/table.js';
import { useSwitchClient } from '../../ClientPicker.js';
import { cn } from '../../../utils.js';
import { fmtWhen } from './advisorApi.js';

/** One line per client: when it was last published, and what still needs doing. */
export function ClientStatusTable({
  statuses,
  activeClientId,
}: {
  statuses: ClientAdvisorStatus[] | null;
  activeClientId: string | null;
}) {
  const switchClient = useSwitchClient();
  return (
    <Card title="מצב הלקוחות">
      {statuses !== null && statuses.length === 0 && <EmptyState label="אין לקוחות" />}
      {statuses !== null && statuses.length > 0 && (
        <Table minWidth="36rem">
          <THeadRow>
            <TH>לקוח</TH>
            <TH>פורסם לאחרונה</TH>
            <TH>הועתק מגרסה</TH>
            <TH>מצב</TH>
          </THeadRow>
          <tbody>
            {statuses.map((s) => {
              const active = s.clientId === activeClientId;
              return (
                <TR key={s.clientId} className={cn(active && 'bg-secondary')}>
                  <TD>
                    <button
                      type="button"
                      onClick={() => switchClient(s.clientId)}
                      aria-current={active ? 'true' : undefined}
                      className="text-start font-semibold underline-offset-2 hover:underline"
                    >
                      {s.displayNameHe}
                    </button>
                  </TD>
                  <TD>{s.publishedAt ? <Ltr>{fmtWhen(s.publishedAt)}</Ltr> : <span className="text-muted-foreground">לא פורסם</span>}</TD>
                  <TD className="tabular-nums">{s.copiedFromVersion ?? '—'}</TD>
                  <TD>
                    <div className="flex flex-wrap gap-1.5">
                      {s.hasUnpublishedEdits && <Badge tone="warning">שינויים שלא פורסמו</Badge>}
                      {s.newerVersionAvailable && (
                        <Badge tone="info">{s.copiedFromVersion === null ? 'טרם הועתקה גרסה' : 'יש גרסה ראשית חדשה יותר'}</Badge>
                      )}
                      {!s.hasUnpublishedEdits && !s.newerVersionAvailable && s.publishedAt && <Badge tone="ok">מעודכן</Badge>}
                    </div>
                  </TD>
                </TR>
              );
            })}
          </tbody>
        </Table>
      )}
    </Card>
  );
}

function Badge({ tone, children }: { tone: 'warning' | 'info' | 'ok'; children: string }) {
  return (
    <span
      className={cn(
        'whitespace-nowrap rounded-full border px-2 py-0.5 text-xs font-semibold',
        tone === 'warning' && 'border-warning/40 bg-warning/10 text-warning',
        tone === 'info' && 'border-ring/40 bg-ring/10 text-foreground',
        tone === 'ok' && 'border-success/40 bg-success/10 text-success',
      )}
    >
      {children}
    </span>
  );
}
